<template>
  <div :class="$style.viewFilters">
    <div
      v-if="metrics.length > 1"
      :class="[$style.metricsFilters, $style.filters]"
    >
      <button
        v-for="metricFilter in metrics"
        :key="'metric-' + metricFilter"
        :class="[
          $style.filter,
          selectedMetricFilter === metricFilter && $style.active,
        ]"
        @click="selectMetricFilter(metricFilter)"
      >
        {{ localizedStrings[metricFilter] }}
      </button>
    </div>
    <div :class="[$style.filters, $style.justifyRight]">
      <div :class="$style.dateFilters">
        <button
          :class="[$style.filter, selectedDateFilter === 30 && $style.active]"
          @click="selectDateFilter(30)"
        >
          {{ localizedStrings['last-30-days'] }}
        </button>
        <button
          :class="[$style.filter, selectedDateFilter === 90 && $style.active]"
          @click="selectDateFilter(90)"
        >
          {{ localizedStrings['last-90-days'] }}
        </button>
      </div>
      <div :class="$style.chartViewFilters">
        <button
          v-if="selectedCategory !== 'community'"
          :class="[$style.filter, selectedView === 'weekly' && $style.active]"
          @click="selectView('weekly')"
        >
          {{ localizedStrings['weekly-view'] }}
        </button>
        <button
          :class="[
            $style.filter,
            selectedView === 'cumulative' && $style.active,
          ]"
          @click="selectView('cumulative')"
        >
          {{ localizedStrings['cumulative-view'] }}
        </button>
      </div>
      <div :class="[$style.select, shouldOpenSelect && $style.open]">
        <button :class="$style.selectHeader" @click="toggleSelect">
          <span>
            {{ localizedStrings['compare-stores'] }}
          </span>
          <IconChevronDown width="16" height="16" :class="$style.chevron" />
        </button>
        <div :class="$style.selectOptionsWrap">
          <ul :class="$style.selectOptions">
            <li
              v-for="store in stores"
              :key="store?.id"
              :class="$style.checkboxInput"
            >
              <label>
                <input
                  id="stores"
                  :name="store?.id"
                  type="checkbox"
                  :checked="selectedStores.has(store?.id)"
                  @click="
                    (e) =>
                      store &&
                      handleSelect(
                        (e.target as HTMLInputElement).checked,
                        store?.id,
                      )
                  "
                />
                <span :class="$style.option">
                  <span :class="$style.storeName">{{ store?.name }}</span>
                  <span :class="$style.storeId">
                    {{ localizedStrings?.['store-id'] || 'Store ID:' }}
                    {{ store?.id }}
                  </span>
                </span>
              </label>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ITabletopOrganization } from '~/types/tabletop'

defineProps<{
  stores: ITabletopOrganization[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedStores: Set<unknown> & Omit<Set<unknown>, keyof Set<any>>
  shouldOpenSelect: boolean
  metrics: string[]
  selectedCategory: string
  selectedMetricFilter: string
  selectedDateFilter: 30 | 90
  selectedView: 'weekly' | 'cumulative'
  selectMetricFilter: (arg: string) => void
  selectDateFilter: (arg: 30 | 90) => void
  selectView: (arg: 'weekly' | 'cumulative') => void
  handleSelect: (arg1: boolean, arg2: string) => void
  toggleSelect: () => void
}>()

const { localizedStrings } = useLocalizedStrings()
</script>

<style lang="scss" module>
.viewFilters {
  display: flex;
  flex-direction: column;
  gap: 16px;

  @include media('sm') {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.filters,
.dateFilters,
.chartViewFilters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @include media('md') {
    flex-wrap: nowrap;
  }
}

.filters {
  width: 100%;

  @include media('sm') {
    width: max-content;
  }

  &.justifyRight {
    @include media('sm') {
      justify-content: flex-end;

      width: 100%;
    }
  }
}

.metricsFilters,
.dateFilters,
.chartViewFilters {
  padding: 6px 8px;
  border-radius: 12px;
  background: #f3f4f6;

  .filter {
    width: 100%;
    line-height: 1.2;
  }
}

.dateFilters,
.chartViewFilters {
  @include media('sm') {
    justify-content: flex-end;
  }
}

.filter {
  all: unset;
  padding: 8px 16px;
  border-radius: 8px;

  &.active,
  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
  }

  &.active {
    background: var(--white);
    color: var(--purple);
    font-weight: 700;
  }

  &:hover {
    background: var(--purple);
    color: var(--white);
    cursor: pointer;
  }

  @include media('sm') {
    white-space: nowrap;
  }
}

.select {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 310px;
  height: 46px;
  background: var(--white);
  border-radius: var(--border-radius);
}

.selectHeader {
  all: unset;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid var(--gray-5);
  border-radius: var(--border-radius);

  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    cursor: pointer;
  }

  .open & {
    border-bottom: 0;
    border-radius: var(--border-radius) var(--border-radius) 0 0;
  }
}

.chevron {
  color: var(--purple);
}

.selectOptionsWrap {
  position: absolute;
  left: 0;
  right: 0;
  top: 45px;
  display: none;
  overflow: hidden;
  padding: 12px 0 0 16px;
  border: 1px solid var(--gray-5);
  border-top: 0;
  border-radius: 0 0 var(--border-radius) var(--border-radius);
  background: var(--white);

  .open & {
    display: block;
  }
}

.selectOptions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px 12px 0;
  max-height: 300px;
  overflow-y: scroll;
}

.checkboxInput {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;

  input {
    margin-top: 6px;
    accent-color: var(--purple-dark);

    &:hover {
      accent-color: var(--purple-light);
    }
  }

  label {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      color: var(--purple-light);
      cursor: pointer;
    }
  }

  .option {
    display: flex;
    flex-direction: column;
    overflow: hidden;

    span {
      overflow: hidden;
      text-wrap: wrap;
    }
  }

  .storeId {
    color: #828282;
  }

  &:has(input:checked) .storeName {
    font-weight: 600;
    color: var(--purple);
  }
}
</style>
